/**
 * useAIGateway Hook
 *
 * Main hook for managing the AI Gateway connection and accessing its services.
 */

import { useCallback } from 'react';
import { useAIGatewayStore } from '../stores/useAIGatewayStore';
import { aiGatewayService } from '../services/aiGateway';
import type {
  STTOptions,
  LLMMessage,
  LLMStreamCallbacks,
  GenerationParams,
  TTSOptions,
  TTSCallbacks,
  PipelineContext,
  PipelineCallbacks,
} from '../types/aiGateway';

export function useAIGateway() {
  const {
    connectionState,
    sessionId,
    capabilities,
    error,
    activeRequests,
    connect: storeConnect,
    disconnect: storeDisconnect,
    setError,
  } = useAIGatewayStore();

  /**
   * Connect to the AI Gateway (auto-connects if not already connected)
   */
  const connect = useCallback(async () => {
    if (connectionState === 'connected') {
      return;
    }
    await storeConnect();
  }, [connectionState, storeConnect]);

  /**
   * Disconnect from the AI Gateway
   */
  const disconnect = useCallback(() => {
    storeDisconnect();
  }, [storeDisconnect]);

  /**
   * Transcribe audio to text
   */
  const transcribe = useCallback(
    async (audio: Blob | string, options?: STTOptions) => {
      if (!aiGatewayService.isConnected()) {
        await connect();
      }
      return aiGatewayService.transcribe(audio, options);
    },
    [connect]
  );

  /**
   * Stream a chat completion
   */
  const streamChat = useCallback(
    async (
      messages: LLMMessage[],
      callbacks: LLMStreamCallbacks,
      generationParams?: GenerationParams
    ) => {
      if (!aiGatewayService.isConnected()) {
        await connect();
      }
      return aiGatewayService.streamChat(messages, callbacks, generationParams);
    },
    [connect]
  );

  /**
   * Synthesize text to speech
   */
  const synthesize = useCallback(
    async (text: string, callbacks: TTSCallbacks, options?: TTSOptions) => {
      if (!aiGatewayService.isConnected()) {
        await connect();
      }
      return aiGatewayService.synthesize(text, callbacks, options);
    },
    [connect]
  );

  /**
   * Execute a full voice turn (STT → LLM → TTS)
   */
  const voiceTurn = useCallback(
    async (
      audio: Blob | string,
      context: PipelineContext,
      callbacks: PipelineCallbacks
    ) => {
      if (!aiGatewayService.isConnected()) {
        await connect();
      }
      return aiGatewayService.voiceTurn(audio, context, callbacks);
    },
    [connect]
  );

  /**
   * Interrupt all active pipelines
   */
  const interrupt = useCallback(() => {
    aiGatewayService.interrupt();
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  /**
   * Check if connected
   */
  const isConnected = connectionState === 'connected';

  /**
   * Check if connecting
   */
  const isConnecting = connectionState === 'connecting' || connectionState === 'reconnecting';

  return {
    // State
    connectionState,
    sessionId,
    capabilities,
    error,
    activeRequests,
    isConnected,
    isConnecting,

    // Actions
    connect,
    disconnect,
    transcribe,
    streamChat,
    synthesize,
    voiceTurn,
    interrupt,
    clearError,
  };
}
